import React, { useState, useRef, useEffect } from "react"
import ReactDOM from "react-dom"
import "./CustomDropdown.css"

type DropdownOption = {
  value: string
  label: string
}

interface StandaloneDropdownProps {
  value: string | null
  onChange: (value: string) => void
  options: DropdownOption[]
  readOnly: boolean
}

export const StandaloneDropdown: React.FC<StandaloneDropdownProps> = ({
  value,
  onChange,
  options,
  readOnly
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const dropdownListRef = useRef<HTMLUListElement | null>(null)

  const updatePosition = () => {
    if (dropdownRef.current && dropdownListRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
      const position = ({
        top: rect.bottom + scrollTop,
        left: rect.left + scrollLeft,
        width: rect.width,
      })
      dropdownListRef.current.style.top = `${position.top}px`
      dropdownListRef.current.style.left = `${position.left}px`
      dropdownListRef.current.style.width = `${position.width}px`
      dropdownListRef.current.style.position = 'absolute'
    }
  }

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      isOpen &&
      !dropdownRef.current?.contains(event.target as Node) &&
      !dropdownListRef.current?.contains(event.target as Node)
    ) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick)
      window.addEventListener("resize", updatePosition)
      window.addEventListener("scroll", updatePosition) // Use capture phase to catch scrolls earlier
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition)
    }
  }, [isOpen])

  useEffect(() => {
    let animationFrameId: number
    const refreshPosition = () => {
      updatePosition()
      animationFrameId = requestAnimationFrame(refreshPosition)
    }
    refreshPosition()
    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [])

  return (
    <div ref={dropdownRef} className={`dropdown-parent ${readOnly ? 'dropdown-readonly' : ''}`}>
      <div className="dropdown-container" onClick={() => !readOnly && setIsOpen((o) => !o)}>
        {value !== null ? options.find((o) => o.value === value)?.label : "Select..."}
        <span className={`dropdown-arrow ${isOpen ? "open" : ""}`}>▼</span>
      </div>
      {isOpen && ReactDOM.createPortal(
        <ul className="dropdown-list" ref={dropdownListRef}>
          {options.map((option) => (
            <li
              key={option.value}
              onClick={() => {
                setIsOpen(false)
                onChange(option.value)
              }}
              className={`dropdown-item ${option.value === value ? "selected" : ""}`}
            >
              {option.label}
            </li>
          ))}
        </ul>
      , document.body)}
    </div>
  )
}
